import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { chatAPI } from 'services/api';
import { RootState } from 'store/rootReducer';

export declare interface GaleriaInterface {
  id: string;
  name: string;
  createdAt: string;
}

export declare interface ArquivoGaleriaInterface {
  id: string;
  createdAt: string;
  storage: {
    id: string;
    fileName: string;
    fileSizeInBytes: number;
    mimeType: string;
    internalUrl: string;
  };
}

type PaginationInfo = {
  page: number;
  total: number;
};

type FetchArquivosByGaleriaParams = {
  galeriaId: string;
  search?: string;
  page?: number;
  limit?: number;
};

type GaleriasState = {
  galerias: GaleriaInterface[];
  loadingGalerias: boolean;
  galeriasRequestId?: string;

  arquivosGaleria: ArquivoGaleriaInterface[];
  arquivosGaleriaPagination: PaginationInfo;
  loadingArquivosGaleria: boolean;
  arquivosGaleriaId?: string;
  arquivosGaleriaRequestId?: string;
};

// async thunks

export const fetchGalerias = createAsyncThunk(
  'galerias/fetchGalerias',
  async () => {
    const response = await chatAPI.get('v2/galleries/select');
    return response.data as GaleriaInterface[];
  },
);

export const fetchArquivosByGaleria = createAsyncThunk(
  'galerias/fetchArquivosByGaleria',
  async (params: FetchArquivosByGaleriaParams) => {
    const { galeriaId } = params;
    const page = params.page ?? 1;
    const response = await chatAPI.get(`v2/galleries/${galeriaId}/files`, {
      params: { search: params.search, page, limit: params.limit },
    });

    return {
      data: response.data.data as ArquivoGaleriaInterface[],
      galeriaId,
      page,
      total: response.data.total as number,
    };
  },
);

// reducer

const initialState = {
  galerias: [],
  loadingGalerias: false,

  arquivosGaleria: [],
  arquivosGaleriaPagination: { page: 1, total: 0 },
  loadingArquivosGaleria: false,
} as GaleriasState;

export const galeriasSlice = createSlice({
  name: 'galerias',
  initialState,
  reducers: {
    resetArquivosGaleria(state) {
      state.arquivosGaleria = initialState.arquivosGaleria;
      state.arquivosGaleriaPagination = initialState.arquivosGaleriaPagination;
      state.loadingArquivosGaleria = initialState.loadingArquivosGaleria;
      state.arquivosGaleriaId = undefined;
      state.arquivosGaleriaRequestId = undefined;
    },
    resetGalerias(state) {
      state.galerias = initialState.galerias;
      state.loadingGalerias = initialState.loadingGalerias;
      state.galeriasRequestId = undefined;
    },
  },
  extraReducers(builder) {
    // Fetch galerias
    builder.addCase(fetchGalerias.pending, (state, action) => {
      state.loadingGalerias = true;
      state.galeriasRequestId = action.meta.requestId;
    });
    builder.addCase(fetchGalerias.fulfilled, (state, action) => {
      if (state.galeriasRequestId !== action.meta.requestId) return;

      state.galerias = action.payload;
      state.loadingGalerias = false;
      state.galeriasRequestId = undefined;
    });
    builder.addCase(fetchGalerias.rejected, (state, action) => {
      if (state.galeriasRequestId !== action.meta.requestId) return;

      state.loadingGalerias = false;
      state.galeriasRequestId = undefined;
    });
    // Fetch arquivos by galeria
    builder.addCase(fetchArquivosByGaleria.pending, (state, action) => {
      state.loadingArquivosGaleria = true;
      state.arquivosGaleriaId = action.meta.arg.galeriaId;
      state.arquivosGaleriaRequestId = action.meta.requestId;
    });
    builder.addCase(fetchArquivosByGaleria.fulfilled, (state, action) => {
      if (
        state.arquivosGaleriaId !== action.payload.galeriaId ||
        state.arquivosGaleriaRequestId !== action.meta.requestId
      )
        return;

      state.arquivosGaleria =
        action.payload.page > 1
          ? [...state.arquivosGaleria, ...action.payload.data]
          : action.payload.data;
      state.arquivosGaleriaPagination = {
        page: action.payload.page,
        total: action.payload.total,
      };
      state.loadingArquivosGaleria = false;
      state.arquivosGaleriaRequestId = undefined;
    });
    builder.addCase(fetchArquivosByGaleria.rejected, (state, action) => {
      if (state.arquivosGaleriaRequestId !== action.meta.requestId) return;

      state.loadingArquivosGaleria = false;
      state.arquivosGaleriaRequestId = undefined;
    });
  },
});

// actions

export const { resetArquivosGaleria, resetGalerias } = galeriasSlice.actions;

// selectors

export const galeriasSelector = (state: RootState) => state.galerias;

export default galeriasSlice.reducer;
